import {
  Button,
  LabeledRightIconInput,
  DatePickerInput,
} from '@apexinternal/ui-components'
import { Icon, Wrap, WrapItem, VStack, FormLabel } from '@chakra-ui/react'
import React, { FormEvent, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { FaSearch } from 'react-icons/fa'
import { useAccountSearch } from '../../hooks'
import {
  getPastDate,
  NOT_LETTER_OR_NUMBER,
  removeSpecialCharacters,
  useFormatMessage,
  formatAccountWithoutDashes,
} from '../../utils'
import { startsWith } from 'lodash'

interface AccountSearchBarProps {
  accountNumber?: string
  hasDatePicker?: boolean
  width?: string
}

const MIN_SEARCH_LENGTH = 3

const AccountSearchBar = ({
  hasDatePicker = false,
  accountNumber,
  width,
}: AccountSearchBarProps) => {
  const {
    accountSearchState: { loadingResults, searchTerm = '' },
    getActivities,
    getBalances,
    getAccountPositions,
    searchAccounts,
  } = useAccountSearch()
  const formatMessage = useFormatMessage()
  const history = useHistory()
  const [date, setDate] = useState<Date | string>('')
  const [search, setSearch] = useState<string>(() => {
    const locationSearch = startsWith(history.location.search, '?q=')
      ? decodeURIComponent(history.location.search.substring('?q='.length))
      : ''
    return accountNumber || locationSearch || searchTerm
  })

  const handleSubmit = (e: FormEvent) => {
    const account = search || accountNumber || ''
    const formattedAccountNumber = formatAccountWithoutDashes(account)
    const processDate = date
      ? new Date(date).toISOString().slice(0, 10)
      : undefined
    const startDate = processDate ? getPastDate(processDate, 5) : undefined
    processDate &&
      startDate &&
      getActivities(formattedAccountNumber, startDate, processDate)
    getBalances(formattedAccountNumber, processDate)
    getAccountPositions(formattedAccountNumber, processDate)
    if (search !== accountNumber) {
      searchAccounts(formattedAccountNumber, history)
    }
    e.preventDefault()
    e.stopPropagation()
  }

  const clearFields = () => {
    setSearch('')
    setDate('')
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="account-searchbar"
      style={{ width }}
      className="hide-on-print"
    >
      <Wrap
        backgroundColor="apex.whiteSmoke"
        borderBottomColor="apex.darkGray"
        borderBottomWidth={1}
        align="flex-end"
        spacing="1rem"
        padding="1rem"
      >
        <WrapItem>
          <LabeledRightIconInput
            fontWeight="bold"
            size="md"
            width="16rem"
            label={formatMessage('accounts.search.label')}
            icon={<Icon as={FaSearch} />}
            placeholder={formatMessage('accounts.search.placeholder')}
            value={search}
            aria-label="search"
            onChange={e => setSearch(removeSpecialCharacters(e.target.value))}
            isRequired
          />
        </WrapItem>
        {hasDatePicker && (
          <WrapItem
            borderLeftColor="apex.darkGray"
            borderLeftWidth={1}
            pl="1rem"
          >
            <VStack alignItems="flex-start">
              <FormLabel style={{ marginBottom: '-0.5rem' }}>
                Process Date
              </FormLabel>
              <DatePickerInput
                onChange={(date: Date | string) => setDate(date)}
                date={date}
                disableFutureDates={true}
              />
            </VStack>
          </WrapItem>
        )}
        <WrapItem>
          <Button
            variant="primary"
            type="submit"
            disabled={
              search.replace(NOT_LETTER_OR_NUMBER, '').length <
              MIN_SEARCH_LENGTH
            }
            isLoading={loadingResults}
          >
            {formatMessage('accounts.search')}
          </Button>
        </WrapItem>
        <WrapItem>
          <Button
            variant="link"
            border={0}
            color="apex.cornflowerBlue"
            px={5}
            onClick={() => clearFields()}
          >
            Clear
          </Button>
        </WrapItem>
      </Wrap>
    </form>
  )
}

export default AccountSearchBar
